import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TourPlacement } from '../components/tour/tour-placement';
import { AuthService } from './auth.service';

export interface TourStep {
  /** The `data-tour` value of the element to highlight. Missing or off-screen, the card shows centred. */
  target: string;
  title: string;
  body: string;
  /** The side the card prefers when there is room for it. */
  placement: TourPlacement;
  /** The target sits in the side nav, which on mobile stays closed until something opens it. */
  inSidenav: boolean;
}

export interface TourView {
  step: TourStep;
  index: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
}

/** How a tour ended. Either one means it is never shown to this user again. */
export type TourOutcome = 'done' | 'skipped';

/** Key in the Clerk user's unsafeMetadata. */
export const TOUR_METADATA_KEY = 'portalTour';

const STORAGE_PREFIX = 'nubo.portalTour.';

/** Everything it points at lives in the app shell, so the tour runs the same on any route. */
export const PORTAL_TOUR_STEPS: readonly TourStep[] = [
  {
    target: 'project-switcher',
    title: 'Welcome! This is your agent',
    body: 'Each project is one AI agent. Switch between your agents or create a new one from here.',
    placement: 'bottom',
    inSidenav: false
  },
  {
    target: 'nav-knowledge-base',
    title: 'Teach it what you know',
    body: 'Upload files, scan your website or write a few notes. Your agent answers from this knowledge.',
    placement: 'right',
    inSidenav: true
  },
  {
    target: 'nav-configuration',
    title: 'Choose how it answers',
    body: "Pick the AI model and write the instructions that set your agent's tone and rules.",
    placement: 'right',
    inSidenav: true
  },
  {
    target: 'nav-integrations',
    title: 'Meet customers where they are',
    body: 'Integrations connect your agent to the channels your customers use, like your website, with WhatsApp coming soon. Set one up to go live.',
    placement: 'right',
    inSidenav: true
  }
];

/**
 * The first-run tour of the portal. It runs once per user: finishing it or skipping it at any
 * step records the outcome, and a recorded outcome keeps it from starting again.
 *
 * The outcome is kept in two places. The Clerk user's unsafeMetadata follows the user to other
 * browsers without needing anything from Nubo.Api; localStorage answers at once on this browser
 * and still holds if the Clerk write fails.
 */
@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly authService = inject(AuthService);
  private readonly steps = PORTAL_TOUR_STEPS;
  private readonly viewSubject = new BehaviorSubject<TourView | null>(null);
  private userId: string | null = null;

  /** The step on screen, or null while no tour runs. */
  readonly view$ = this.viewSubject.asObservable();

  /** Starts the tour unless the signed-in user has already finished or skipped it. */
  async startIfNew(): Promise<void> {
    if (this.viewSubject.value) return;

    const user = await this.authService.getCurrentUser();
    if (!user) return;
    this.userId = user.id;

    if (localStorage.getItem(STORAGE_PREFIX + user.id)) return;

    const recorded = user.unsafeMetadata?.[TOUR_METADATA_KEY] as { outcome?: TourOutcome } | undefined;
    if (recorded?.outcome) {
      localStorage.setItem(STORAGE_PREFIX + user.id, recorded.outcome);
      return;
    }

    this.show(0);
  }

  next(): void {
    const view = this.viewSubject.value;
    if (!view) return;
    if (view.isLast) this.finish('done');
    else this.show(view.index + 1);
  }

  back(): void {
    const view = this.viewSubject.value;
    if (view && !view.isFirst) this.show(view.index - 1);
  }

  /** Ends the tour and records it, so it does not start again. */
  finish(outcome: TourOutcome): void {
    if (!this.viewSubject.value) return;
    this.viewSubject.next(null);

    if (!this.userId) return;
    localStorage.setItem(STORAGE_PREFIX + this.userId, outcome);
    // Best effort: the local flag already stops it here; the metadata carries it to other browsers.
    this.authService
      .saveUserMetadata(TOUR_METADATA_KEY, { outcome, at: new Date().toISOString() })
      .catch(() => undefined);
  }

  /** Hides the tour without recording anything — the shell is going away (sign-out). */
  close(): void {
    this.viewSubject.next(null);
  }

  private show(index: number): void {
    this.viewSubject.next({
      step: this.steps[index],
      index,
      total: this.steps.length,
      isFirst: index === 0,
      isLast: index === this.steps.length - 1
    });
  }
}
